import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { ConfirmationDialog } from '@/components/dialogBox';
import { RootState } from '@/store/store';
import {
  ThemeRouteContributorCapabilities,
  ThemeRouteContributorGrant,
  ThemeRouteContributorGrantStatus,
} from '@/types/theme-routes.types';
import {
  useAcknowledgeThemeRouteContributorGrantMutation,
  useCreateThemeRouteContributorGrantMutation,
  useDeleteThemeRouteContributorGrantMutation,
  useGetThemeRouteContributorGrantsQuery,
  useGetThemeRouteEligibleContributorDmosQuery,
  useUpdateThemeRouteContributorGrantMutation,
} from '@/services/ThemeRoutesService';

const CAPABILITY_OPTIONS: {
  key: keyof ThemeRouteContributorCapabilities;
  label: string;
}[] = [
  { key: 'can_view', label: 'View' },
  { key: 'can_edit', label: 'Edit' },
  { key: 'can_request_approval', label: 'Request approval' },
  { key: 'can_review', label: 'Review / approve / reject' },
  { key: 'can_publish', label: 'Publish' },
];

const EMPTY_CAPABILITIES: ThemeRouteContributorCapabilities = {
  can_view: true,
  can_edit: true,
  can_request_approval: false,
  can_review: false,
  can_publish: false,
};

const STATUS_LABEL: Record<ThemeRouteContributorGrantStatus, string> = {
  pending: 'Pending',
  active: 'Active',
  revoked: 'Revoked',
};

interface ThemeRouteContributorGrantsPanelProps {
  routeId: string;
  readOnly?: boolean;
}

const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const maybe = error as { status?: number; originalStatus?: number };
  return maybe.status ?? maybe.originalStatus;
};

const orgLabel = (grant: ThemeRouteContributorGrant): string => {
  const name = grant.targetDmoAdmin?.name?.trim();
  const email = grant.targetDmoAdmin?.email?.trim();
  if (name && email) return `${name} (${email})`;
  return name || email || grant.target_dmo_admin_id;
};

const ThemeRouteContributorGrantsPanel = ({
  routeId,
  readOnly = false,
}: ThemeRouteContributorGrantsPanelProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const currentUserId = useSelector(
    (state: RootState) => state.auth.userData?.id
  );
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [draftCapabilities, setDraftCapabilities] =
    useState<ThemeRouteContributorCapabilities>(EMPTY_CAPABILITIES);
  const [revokeState, setRevokeState] = useState<{
    isOpen: boolean;
    grantId: string | null;
  }>({ isOpen: false, grantId: null });

  const {
    data: grantsResult,
    isLoading: isGrantsLoading,
    isError: isGrantsError,
    error: grantsError,
  } = useGetThemeRouteContributorGrantsQuery(routeId);

  const grants = useMemo(() => grantsResult?.grants ?? [], [grantsResult]);
  const isForbidden = getErrorStatus(grantsError) === 403;

  const visibleGrants = useMemo(
    () => grants.filter((g) => g.status !== 'revoked'),
    [grants]
  );
  const revokedGrants = useMemo(
    () => grants.filter((g) => g.status === 'revoked'),
    [grants]
  );

  // Server-decided: only the owning organisation / platform admin may invite or revoke.
  const canManageInvites =
    !readOnly && !isForbidden && !!grantsResult?.canManage;

  const { data: eligibleOrgs = [], isLoading: isEligibleLoading } =
    useGetThemeRouteEligibleContributorDmosQuery(routeId, {
      skip: !canManageInvites || isForbidden || isGrantsError,
    });

  const [createGrant, { isLoading: isCreating }] =
    useCreateThemeRouteContributorGrantMutation();
  const [updateGrant, { isLoading: isUpdating }] =
    useUpdateThemeRouteContributorGrantMutation();
  const [acknowledgeGrant, { isLoading: isAcknowledging }] =
    useAcknowledgeThemeRouteContributorGrantMutation();
  const [deleteGrant, { isLoading: isDeleting }] =
    useDeleteThemeRouteContributorGrantMutation();

  const capabilitySummary = useMemo(() => {
    return (grant: ThemeRouteContributorGrant) =>
      CAPABILITY_OPTIONS.filter((option) => grant[option.key])
        .map((option) => option.label)
        .join(', ') || 'None';
  }, []);

  if (isForbidden) {
    return null;
  }

  if (isGrantsError) {
    return (
      <div className="card">
        <div className="card-header">
          <div className="card-title text-base font-semibold text-gray-800">
            Contributor access
          </div>
        </div>
        <div className="card-body">
          <p className="text-sm text-gray-600">
            Contributor grants could not be loaded. Only the Theme Route owner
            or a Platform Admin can manage cross-organisation collaboration.
          </p>
        </div>
      </div>
    );
  }

  const handleCreate = async () => {
    if (!selectedOrgId) {
      enqueueSnackbar('Select an organisation to invite', {
        variant: 'warning',
      });
      return;
    }
    if (!Object.values(draftCapabilities).some(Boolean)) {
      enqueueSnackbar('Select at least one capability', { variant: 'warning' });
      return;
    }

    try {
      await createGrant({
        id: routeId,
        data: {
          target_dmo_admin_id: selectedOrgId,
          ...draftCapabilities,
        },
      }).unwrap();
      enqueueSnackbar('Contributor invite sent (pending acknowledgement)', {
        variant: 'success',
      });
      setSelectedOrgId('');
      setDraftCapabilities(EMPTY_CAPABILITIES);
    } catch {
      enqueueSnackbar('Failed to create contributor invite', {
        variant: 'error',
      });
    }
  };

  const handleGrantAllEligible = async () => {
    if (!eligibleOrgs.length) {
      enqueueSnackbar('No eligible organisations to invite', {
        variant: 'info',
      });
      return;
    }
    if (!Object.values(draftCapabilities).some(Boolean)) {
      enqueueSnackbar('Select at least one capability', { variant: 'warning' });
      return;
    }

    try {
      for (const org of eligibleOrgs) {
        await createGrant({
          id: routeId,
          data: {
            target_dmo_admin_id: org.id,
            ...draftCapabilities,
          },
        }).unwrap();
      }
      enqueueSnackbar(
        `Invited ${eligibleOrgs.length} eligible organisation${eligibleOrgs.length === 1 ? '' : 's'}`,
        { variant: 'success' }
      );
      setSelectedOrgId('');
    } catch {
      enqueueSnackbar('Failed to invite all eligible organisations', {
        variant: 'error',
      });
    }
  };

  const handleCapabilityToggle = async (
    grant: ThemeRouteContributorGrant,
    key: keyof ThemeRouteContributorCapabilities,
    checked: boolean
  ) => {
    try {
      await updateGrant({
        id: routeId,
        grantId: grant.id,
        data: { [key]: checked },
      }).unwrap();
      enqueueSnackbar('Contributor grant updated', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to update contributor grant', {
        variant: 'error',
      });
    }
  };

  const handleAcknowledge = async (grantId: string) => {
    try {
      await acknowledgeGrant({ id: routeId, grantId }).unwrap();
      enqueueSnackbar('Contributor invite acknowledged', {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('Failed to acknowledge invite', { variant: 'error' });
    }
  };

  const handleConfirmRevoke = async () => {
    if (!revokeState.grantId) return;
    try {
      await deleteGrant({
        id: routeId,
        grantId: revokeState.grantId,
      }).unwrap();
      enqueueSnackbar('Contributor grant revoked', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to revoke contributor grant', {
        variant: 'error',
      });
    } finally {
      setRevokeState({ isOpen: false, grantId: null });
    }
  };

  const statusBadgeClass = (status: ThemeRouteContributorGrantStatus) => {
    if (status === 'active') return 'badge badge-success badge-sm';
    if (status === 'pending') return 'badge badge-warning badge-outline';
    return 'badge badge-secondary badge-outline';
  };

  const renderGrantRow = (
    grant: ThemeRouteContributorGrant,
    options: { showCapabilities: boolean; showRevoke: boolean }
  ) => {
    const isMine =
      !!currentUserId && grant.target_dmo_admin_id === currentUserId;
    const canAcknowledge = isMine && grant.status === 'pending';

    return (
      <li
        key={grant.id}
        className="border border-gray-200 rounded-lg p-3 bg-gray-100"
      >
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm font-medium text-gray-900">
                {orgLabel(grant)}
              </div>
              <span className={statusBadgeClass(grant.status)}>
                {STATUS_LABEL[grant.status] || grant.status}
              </span>
            </div>
            {(readOnly || !options.showCapabilities) && (
              <div className="text-xs text-gray-600 mt-1">
                {capabilitySummary(grant)}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {canAcknowledge && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={isAcknowledging}
                onClick={() => handleAcknowledge(grant.id)}
              >
                Acknowledge
              </button>
            )}
            {options.showRevoke && grant.status !== 'revoked' && (
              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={isDeleting}
                onClick={() =>
                  setRevokeState({ isOpen: true, grantId: grant.id })
                }
              >
                Revoke
              </button>
            )}
          </div>
        </div>
        {options.showCapabilities && grant.status !== 'revoked' && (
          <div className="flex flex-wrap gap-4">
            {CAPABILITY_OPTIONS.map((option) => (
              <label
                key={option.key}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  className="checkbox cursor-pointer"
                  checked={!!grant[option.key]}
                  disabled={isUpdating || grant.status === 'pending'}
                  onChange={(event) =>
                    handleCapabilityToggle(
                      grant,
                      option.key,
                      event.target.checked
                    )
                  }
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        )}
      </li>
    );
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title text-base font-semibold text-gray-800">
              Contributor access
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Invite other DMO organisations to collaborate on this route.
              Invites start as Pending until acknowledged. Contributors may only
              edit harbours in their own scope.
            </p>
          </div>
        </div>
        <div className="card-body space-y-6">
          {isGrantsLoading ? (
            <p className="text-sm text-gray-600">
              Loading contributor grants...
            </p>
          ) : visibleGrants.length === 0 && revokedGrants.length === 0 ? (
            <p className="text-sm text-gray-500">
              No contributor grants yet. Other organisations cannot manage this
              route until you invite them.
            </p>
          ) : (
            <>
              {visibleGrants.length > 0 && (
                <ul className="space-y-3">
                  {visibleGrants.map((grant) =>
                    renderGrantRow(grant, {
                      showCapabilities: canManageInvites && !readOnly,
                      showRevoke: canManageInvites,
                    })
                  )}
                </ul>
              )}
              {canManageInvites && revokedGrants.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Revoked
                  </div>
                  <ul className="space-y-3 opacity-70">
                    {revokedGrants.map((grant) =>
                      renderGrantRow(grant, {
                        showCapabilities: false,
                        showRevoke: false,
                      })
                    )}
                  </ul>
                </div>
              )}
            </>
          )}

          {canManageInvites && (
            <div className="border border-dashed border-gray-300 rounded-lg p-4 space-y-4">
              <div className="text-sm font-medium text-gray-800">
                Invite an eligible organisation
              </div>
              {isEligibleLoading ? (
                <p className="text-sm text-gray-600">
                  Loading eligible organisations...
                </p>
              ) : eligibleOrgs.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No additional DMOs to invite. All other active DMO
                  organisations already have a pending or active invite, or none
                  exist besides the route owner.
                </p>
              ) : (
                <>
                  <div className="flex flex-col gap-2 max-w-xl">
                    <label
                      htmlFor="contributor-org-select"
                      className="text-sm font-medium text-gray-700"
                    >
                      Eligible DMO
                    </label>
                    <select
                      id="contributor-org-select"
                      className="select"
                      value={selectedOrgId}
                      onChange={(event) => setSelectedOrgId(event.target.value)}
                    >
                      <option value="">Select a DMO...</option>
                      {eligibleOrgs.map((org) => (
                        <option key={org.id} value={org.id}>
                          {(org.name || org.email || org.id) +
                            (org.name && org.email ? ` (${org.email})` : '')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-wrap gap-4">
                    {CAPABILITY_OPTIONS.map((option) => (
                      <label
                        key={option.key}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <input
                          type="checkbox"
                          className="checkbox cursor-pointer"
                          checked={draftCapabilities[option.key]}
                          onChange={(event) =>
                            setDraftCapabilities((prev) => ({
                              ...prev,
                              [option.key]: event.target.checked,
                            }))
                          }
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={isCreating || !selectedOrgId}
                      onClick={handleCreate}
                    >
                      Invite selected
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      disabled={isCreating || eligibleOrgs.length === 0}
                      onClick={handleGrantAllEligible}
                    >
                      Invite all eligible
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmationDialog
        open={revokeState.isOpen}
        onClose={() => setRevokeState({ isOpen: false, grantId: null })}
        onConfirm={handleConfirmRevoke}
        message="Revoke contributor access? This removes the organisation's delegated Theme Route authority only. The route, harbours and POIs are not changed."
        confirmButtonText="Revoke"
        cancelButtonText="Cancel"
        confirmButtonVariant="destructive"
        isLoading={isDeleting}
      />
    </>
  );
};

export default ThemeRouteContributorGrantsPanel;
